import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import Seo from '@/components/feature/Seo';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ArticleCard from '@/components/feature/ArticleCard';
import JsonLd from '@/components/feature/JsonLd';
import {
  getArticleById,
  categoryName,
  formatHeat,
  formatLike,
  relatedArticles,
  hotArticles,
} from '@/utils/content';
import { site } from '@/mocks/site';

export default function ArticleDetailPage() {
  const { id = '' } = useParams();
  const article = getArticleById(id);
  const [liked, setLiked] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!article) {
    return (
      <PageLayout>
        <Seo title="内容不存在" description="你访问的内容不存在或已被移除。" />
        <div className="mx-auto flex max-w-7xl flex-col items-center px-4 py-24 text-center">
          <i className="ri-error-warning-line text-5xl text-primary-500" />
          <h1 className="mt-4 text-2xl font-bold text-ink">这篇盘点被风刮走了</h1>
          <Link to="/" className="mt-6 rounded-full bg-primary-500 px-6 py-2.5 text-sm font-bold text-cream-50 hover:bg-primary-600">
            回首页继续逛
          </Link>
        </div>
      </PageLayout>
    );
  }

  const related = relatedArticles(article, 3);
  const sidebarHot = hotArticles(6);
  const cat = categoryName(article.categoryId);

  const likeCount = article.likeCount + (liked ? 1 : 0);

  const share = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  return (
    <PageLayout>
      <Seo title={article.title} description={article.excerpt} keywords={`校园爆料,${cat},${article.tags.slice(0, 2).join(',')}`} />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.excerpt,
          image: [article.cover],
          datePublished: `${article.date}T10:00:00+08:00`,
          author: { '@type': 'Organization', name: site.brand },
          publisher: { '@type': 'Organization', name: site.brand },
        }}
      />

      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
        <Breadcrumbs
          items={[
            { label: '首页', to: '/' },
            { label: cat, to: `/column/${article.categoryId}` },
            { label: article.title },
          ]}
        />

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px]">
          {/* main */}
          <article className="overflow-hidden rounded-2xl border border-cream-200 bg-white">
            <div className="relative">
              <img
                src={article.cover}
                alt={article.coverAlt}
                className="aspect-[16/8] w-full object-cover object-top"
              />
              <span className="absolute left-4 top-4 rounded-full bg-cream-50/90 px-3 py-1 text-xs font-bold text-primary-700">
                {cat}
              </span>
            </div>

            <div className="p-5 md:p-8">
              <h1 className="text-2xl font-black leading-snug text-ink md:text-3xl">{article.title}</h1>

              <div className="mt-4 flex flex-wrap items-center gap-4 border-b border-cream-100 pb-5 text-sm text-ink-600">
                <span className="flex items-center gap-2">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-user-smile-line" />
                  </span>
                  <span className="font-medium text-ink">{article.author}</span>
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line" /> {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line" /> 阅读约 {article.readMin} 分钟
                </span>
                <span className="flex items-center gap-1 text-primary-600">
                  <i className="ri-fire-fill" /> {formatHeat(article.heat)}
                </span>
              </div>

              <p className="mt-6 rounded-xl bg-cream-100 p-4 text-base leading-relaxed text-ink-600">
                {article.excerpt}
              </p>

              <div className="mt-4 space-y-5 text-base leading-8 text-ink">
                {article.content.map((para, i) => (
                  <div key={i}>
                    {i === 1 && (
                      <blockquote className="my-5 rounded-xl border-l-4 border-primary-500 bg-primary-50 p-4 font-medium text-primary-800">
                        <i className="ri-double-quotes-l mr-1" />
                        {article.pullquote}
                      </blockquote>
                    )}
                    <p>{para}</p>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap gap-2 border-t border-cream-100 pt-5">
                {article.tags.map((t) => (
                  <span key={t} className="rounded-full bg-cream-100 px-3 py-1 text-xs text-ink-600">
                    #{t}
                  </span>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setLiked((v) => !v)}
                  className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${
                    liked
                      ? 'bg-secondary-500 text-cream-50'
                      : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'
                  }`}
                >
                  <i className={liked ? 'ri-heart-3-fill' : 'ri-heart-3-line'} />
                  {formatLike(likeCount)} 有用
                </button>
                <button
                  type="button"
                  onClick={share}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-cream-100 px-5 py-2.5 text-sm font-bold text-ink hover:bg-cream-200"
                >
                  <i className={copied ? 'ri-check-line text-primary-600' : 'ri-share-line'} />
                  {copied ? '链接已复制' : '分享'}
                </button>
                <Link
                  to={`/column/${article.categoryId}`}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-cream-300 px-5 py-2.5 text-sm font-bold text-ink-600 hover:border-primary-300 hover:text-primary-700"
                >
                  更多{cat}盘点 <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </article>

          {/* sidebar */}
          <aside className="space-y-6">
            <div className="rounded-2xl border border-cream-200 bg-white p-5">
              <h2 className="mb-3 flex items-center gap-2 text-sm font-bold text-ink">
                <i className="ri-fire-fill text-primary-500" /> 全站热门
              </h2>
              <div className="space-y-3">
                {sidebarHot.slice(0, 5).map((a) => (
                  <ArticleCard key={a.id} article={a} variant="row" showCategory={false} />
                ))}
              </div>
            </div>

            <div className="rounded-2xl bg-gradient-to-br from-secondary-500 to-primary-500 p-5 text-cream-50">
              <p className="font-bold">正在热读这篇文章？</p>
              <p className="mt-1 text-sm text-cream-100">把身边的名场面告诉我们，说不定下一篇就是你的同学。</p>
              <a
                href={`mailto:${site.email}`}
                className="mt-4 inline-block whitespace-nowrap rounded-full bg-cream-50 px-5 py-2 text-sm font-bold text-primary-700 hover:scale-105"
              >
                我要投稿
              </a>
            </div>
          </aside>
        </div>

        {/* related */}
        <section className="mt-10">
          <div className="mb-4 flex items-end justify-between">
            <h2 className="font-display text-xl text-ink md:text-2xl">你可能还会喜欢</h2>
            <Link to="/" className="text-sm font-bold text-primary-600 hover:text-primary-700">
              更多内容 <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((a) => (
              <ArticleCard key={a.id} article={a} />
            ))}
          </div>
        </section>
      </div>
    </PageLayout>
  );
}